/**
 * Citation-derived fields of the trend snapshot the dashboard persists to
 * `citation_monitor_history`.
 *
 * These live outside the page component for one reason: they are a persistence
 * CONTRACT, and the contract has to be testable without rendering the audit
 * dashboard.
 *
 * The contract: a Citation Check run that withheld its grade contributes
 * NOTHING here. `scoreStatus: 'withheld'` means the evaluation was too
 * incomplete to grade — a provider that answered 1 of 4 prompts has no
 * defensible mention rate — so persisting its per-provider numbers would bake
 * an unearned 100% into trend history permanently. The unified score can still
 * be a real number while Citation Check is withheld (another tool graded), so
 * the unified-score guard at the call site does not cover this; the withheld
 * check does.
 *
 * This follows the same withheld-state rule as the inline Prompt SOV gate
 * beside it at the call site, which spells the check out by hand. Here the
 * check goes through the shared `hasUsableScoreData` predicate from
 * `./tool-scores` — the source of truth for "may this tool's evidence be
 * consumed".
 */

import { hasUsableScoreData } from './tool-scores'

interface CitationQuery {
  sentimentDetail?: { tone: string }
}

interface CitationProvider {
  provider: string
  overallMentionRate: number
  queries?: CitationQuery[]
}

export interface CitationTrendFields {
  /** Per-provider mention rate as a percentage. Empty when withheld. */
  providerScores: Record<string, number>
  /** Per-provider sentiment tones. Empty when withheld. */
  sentimentBreakdown: Record<string, string[]>
}

/**
 * Build the citation-derived trend fields. Pure.
 *
 * Returns empty records for withheld, errored, absent, or ungradeable Citation
 * Check data — never a partial or coerced rate.
 */
export function buildCitationTrendFields(
  citationData: unknown
): CitationTrendFields {
  if (!hasUsableScoreData(citationData)) {
    return { providerScores: {}, sentimentBreakdown: {} }
  }

  const providers = (citationData as { providers?: CitationProvider[] })
    .providers
  if (!Array.isArray(providers)) {
    return { providerScores: {}, sentimentBreakdown: {} }
  }

  const providerScores: Record<string, number> = {}
  const sentimentBreakdown: Record<string, string[]> = {}

  for (const p of providers) {
    if (!p?.provider) continue
    providerScores[p.provider] = Math.round(p.overallMentionRate * 100)
    sentimentBreakdown[p.provider] =
      p.queries
        ?.filter(q => q.sentimentDetail)
        .map(q => q.sentimentDetail?.tone || 'neutral') || []
  }

  return { providerScores, sentimentBreakdown }
}
